import { getToken } from "../utils/auth";
import { API_BASE_URL } from "./URL_Header";

const authHeaders = () => ({
  Authorization: `Bearer ${getToken()}`,
  "Content-Type": "application/json",
});

async function readError(response) {
  try {
    const data = await response.json();
    return data.message || data.title || `خطأ ${response.status}`;
  } catch {
    return `خطأ ${response.status}`;
  }
}

async function request(path, options = {}) {
  const response = await fetch(`${API_BASE_URL}/hr${path}`, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) },
  });
  if (!response.ok) throw new Error(await readError(response));
  if (response.status === 204) return null;
  const type = response.headers.get("content-type") || "";
  if (type.includes("application/json")) return response.json();
  return response.blob();
}

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, value);
  });
  const text = search.toString();
  return text ? `?${text}` : "";
};

export const fetchHrAccess = () => request("/access");
export const fetchHrPermissions = () => request("/permissions");
export const fetchHrGrants = (userId) => request(`/permissions/${userId}`);
export const saveHrGrants = (body) => request("/permissions", { method: "PUT", body: JSON.stringify(body) });

export const fetchEmployees = (params) => request(`/employees${query(params)}`);
export const fetchEmployee = (id) => request(`/employees/${id}`);
export const fetchLinkableUsers = () => request("/employees/linkable-users");
export const createEmployee = (body) => request("/employees", { method: "POST", body: JSON.stringify(body) });
export const updateEmployee = (id, body) => request(`/employees/${id}`, { method: "PUT", body: JSON.stringify(body) });
export const changeEmployeeStatus = (id, body) => request(`/employees/${id}/status`, { method: "POST", body: JSON.stringify(body) });
export const transferEmployee = (id, body) => request(`/employees/${id}/transfer`, { method: "POST", body: JSON.stringify(body) });
export const fetchEmployeeActivity = (id, params) => request(`/employees/${id}/activity${query(params)}`);
export const fetchEmployeeNotes = (id) => request(`/employees/${id}/notes`);
export const createEmployeeNote = (body) => request("/notes", { method: "POST", body: JSON.stringify(body) });

export const fetchDepartments = () => request("/departments");
export const saveDepartment = (body, id) => request(id ? `/departments/${id}` : "/departments", { method: id ? "PUT" : "POST", body: JSON.stringify(body) });

export const fetchAttendance = (params) => request(`/attendance${query(params)}`);
export const checkIn = () => request("/attendance/check-in", { method: "POST" });
export const checkOut = () => request("/attendance/check-out", { method: "POST" });
export const recordAttendance = (body) => request("/attendance/manual", { method: "POST", body: JSON.stringify(body) });
export const fetchCorrections = (status) => request(`/attendance/corrections${query({ status })}`);
export const requestCorrection = (body) => request("/attendance/corrections", { method: "POST", body: JSON.stringify(body) });
export const reviewCorrection = (id, body) => request(`/attendance/corrections/${id}/review`, { method: "POST", body: JSON.stringify(body) });
export const fetchLeave = (status) => request(`/leave${query({ status })}`);
export const requestLeave = (body) => request("/leave", { method: "POST", body: JSON.stringify(body) });
export const reviewLeave = (id, body) => request(`/leave/${id}/review`, { method: "POST", body: JSON.stringify(body) });

export const saveSalary = (body) => request("/salary-structures", { method: "POST", body: JSON.stringify(body) });
export const fetchPayrollPeriods = () => request("/payroll/periods");
export const createPayrollPeriod = (year, month) => request(`/payroll/periods${query({ year, month })}`, { method: "POST" });
export const fetchPayrollPeriod = (id) => request(`/payroll/periods/${id}`);
export const calculatePayroll = (id, body) => request(`/payroll/periods/${id}/calculate`, { method: "POST", body: JSON.stringify(body) });
export const submitPayroll = (id) => request(`/payroll/periods/${id}/submit`, { method: "POST" });
export const approvePayroll = (id) => request(`/payroll/periods/${id}/approve`, { method: "POST" });
export const closePayroll = (id) => request(`/payroll/periods/${id}/close`, { method: "POST" });
export const payPayrollEntry = (id, body) => request(`/payroll/entries/${id}/payments`, { method: "POST", body: JSON.stringify(body) });
export const adjustPayrollEntry = (id, body) => request(`/payroll/entries/${id}/adjustments`, { method: "POST", body: JSON.stringify(body) });

export const fetchAdvances = (params) => request(`/advances${query(params)}`);
export const createAdvance = (body) => request("/advances", { method: "POST", body: JSON.stringify(body) });
export const approveAdvance = (id) => request(`/advances/${id}/approve`, { method: "POST" });
export const rejectAdvance = (id) => request(`/advances/${id}/reject`, { method: "POST" });
export const disburseAdvance = (id, body) => request(`/advances/${id}/disburse`, { method: "POST", body: JSON.stringify(body || {}) });
export const repayAdvance = (id, body) => request(`/advances/${id}/repayments`, { method: "POST", body: JSON.stringify(body) });
export const fetchAdvanceRepayments = (id) => request(`/advances/${id}/repayments`);

export const fetchOvertime = (params) => request(`/overtime${query(params)}`);
export const createOvertime = (body) => request("/overtime", { method: "POST", body: JSON.stringify(body) });
export const approveOvertime = (id) => request(`/overtime/${id}/approve`, { method: "POST" });
export const rejectOvertime = (id) => request(`/overtime/${id}/reject`, { method: "POST" });

export const fetchKpis = (departmentId) => request(`/kpis${query({ departmentId })}`);
export const saveKpi = (body, id) => request(id ? `/kpis/${id}` : "/kpis", { method: id ? "PUT" : "POST", body: JSON.stringify(body) });
export const saveKpiEntry = (body) => request("/kpi-entries", { method: "POST", body: JSON.stringify(body) });
export const fetchReports = (params) => request(`/reports${query(params)}`);
export const createReport = (body) => request("/reports", { method: "POST", body: JSON.stringify(body) });
export const updateReport = (id, body) => request(`/reports/${id}`, { method: "PUT", body: JSON.stringify(body) });
export const submitReport = (id) => request(`/reports/${id}/submit`, { method: "POST" });
export const approveReport = (id) => request(`/reports/${id}/approve`, { method: "POST" });

export const fetchHrDashboard = (params) => request(`/dashboard${query(params)}`);
export const fetchHrSettings = () => request("/settings");
export const saveJobTitle = (body, id) => request(id ? `/settings/job-titles/${id}` : "/settings/job-titles", { method: id ? "PUT" : "POST", body: JSON.stringify(body) });
export const saveEmploymentType = (body) => request("/settings/employment-types", { method: "POST", body: JSON.stringify(body) });
export const saveLeaveType = (body) => request("/settings/leave-types", { method: "POST", body: JSON.stringify(body) });
export const saveSchedule = (body) => request("/settings/schedules", { method: "POST", body: JSON.stringify(body) });
export const saveHrSetting = (body) => request("/settings", { method: "PUT", body: JSON.stringify(body) });
export const saveComponentTemplate = (body) => request("/settings/component-templates", { method: "POST", body: JSON.stringify(body) });

export function payrollExportUrl(id) {
  return `${API_BASE_URL}/hr/payroll/periods/${id}/export`;
}

export function payslipUrl(id) {
  return `${API_BASE_URL}/hr/payroll/entries/${id}/payslip`;
}

export async function downloadHrFile(path, filename) {
  const response = await fetch(`${API_BASE_URL}/hr${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!response.ok) throw new Error(await readError(response));
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
